/**
 * Backend dashboard API — typed TanStack Query hooks.
 *
 * Five hooks total:
 *   - useDashboardSummary             — first-trust headline figures (legacy, single-summary)
 *   - useDashboardHoldings            — GET /v1/app/dashboard/holdings?trustId=
 *   - useDashboardAllocation          — GET /v1/app/dashboard/allocation?trustId=
 *   - useDashboardDistributionsRhythm — GET /v1/app/dashboard/distributions-rhythm?trustId=
 *   - useDashboardTrustSummary        — GET /v1/app/dashboard/trust-summary?trustId=
 *
 * The four trust-scoped hooks land in Phase 3 Task 3.3 (2026-05-17) to
 * close the P0 "every signed-in user sees Anna van der Merwe's portfolio"
 * leak flagged in the 2026-05-16 audit. They mirror the response envelopes
 * in `backend/src/handlers/dashboard-{holdings,allocation,distributions-rhythm,trust-summary}.ts`.
 *
 * ESTATE ownership model (2026-07-11 ruling): `DashboardSummary` gained
 * `earmarkedValue` / `personOwnedValue` / `estateValue` (tenant-wide figures;
 * `totalValue` / `claimedPendingValue` stay primary-trust-scoped, unchanged),
 * and this module exports the pure `estateSplitParts` helper the hero + the
 * estate-assets card both use to render the "R x registered · R y transfer in
 * progress · R z personal" split honestly. See `../estate.ts` for the
 * separate tenant-wide asset READ (`useEstateAssets`) this feeds off of.
 * DEFECT-A FIX (2026-07-12): `registeredValue` was added alongside the other
 * three tenant-wide fields — the split's "registered" clause now has an
 * honest source that reconciles against `estateValue` for a multi-trust book
 * (see `hero.tsx` for the fallback when an old backend hasn't shipped it).
 */

import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from './client';
import { formatZarCents } from '../fmt-r';

// Re-export the canonical ZAR formatter (CUX-1, 2026-05-21). The single
// implementation now lives in `lib/fmt-r.ts`; this re-export keeps the
// many existing `import { formatZarCents } from '../../lib/api'` call-sites
// working without reaching across modules.
export { formatZarCents };

// 503 from the dashboard holdings/allocation handlers means the Cosmos
// holdings container is not yet deployed for this environment
// (HOLDINGS_NOT_CONFIGURED). Retrying won't help — the container is
// either there or it isn't — and the default 3x retry just spams the
// console with three 503s per page load. Fail fast and let the card
// render its empty-state.
function failFastOn5xx(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError && error.status >= 500) return false;
  return failureCount < 3;
}

// TRUSTEE-09: for the legacy single-summary hook, ANY typed HTTP status is
// terminal — a 404 means "no trust yet" (→ set-up CTA), a 403 means "no tenant
// assignment", and a 5xx won't self-heal on a 3x retry. Only a status-less
// error (a real network blip / abort) is worth retrying a couple of times.
function failFastOn4xxAnd5xx(failureCount: number, error: unknown): boolean {
  if (error instanceof ApiError) return false;
  return failureCount < 2;
}

// ── /v1/dashboard/summary (legacy, kept for hero + other call-sites) ─────────

export interface DashboardSummary {
  trustName: string;
  /** The HONEST registered/verified total (whole Rand). Never includes
   *  claimed-pending value — see `claimedPendingValue`. PRIMARY-TRUST-SCOPED
   *  (unlike `estateValue` below, which is tenant-wide) — this is the honest
   *  AUM anchor the hero never silently swaps out. */
  totalValue: number;
  /** Wave-6 row 4: the SECOND figure — the sum of the primary trust's
   *  `earmarked_for_trust` asset values (whole Rand), shown ALONGSIDE
   *  `totalValue`, never added into it. `0` when there are no pending assets
   *  (the hero zero-suppresses the line). Absent from an old backend → treat
   *  as 0. KEPT for wire compat (ESTATE ownership model, 2026-07-11) — see
   *  `earmarkedValue` for the tenant-wide equivalent that feeds `estateValue`. */
  claimedPendingValue?: number;
  /** ESTATE ownership model DEFECT-A FIX (2026-07-12): the registered total
   *  summed ACROSS ALL of the user's trusts in the tenant — the tenant-wide
   *  sibling of `totalValue` (which stays PRIMARY-TRUST-SCOPED). Feeds the
   *  estate split's "registered to trusts" clause so it reconciles against
   *  `estateValue` even for a client with 2+ registered trusts. Absent from
   *  an old backend: callers derive it as
   *  `estateValue - earmarkedValue - personOwnedValue`. */
  registeredValue?: number;
  /** ESTATE ownership model (2026-07-11 ruling): the sum of
   *  `earmarked_for_trust` asset values ACROSS ALL of the user's trusts in the
   *  tenant (unlike `claimedPendingValue`, not limited to the primary trust —
   *  the client's ESTATE is the tenant-level container). `0` on none / absent
   *  from an old backend (treat as 0, same degrade as `claimedPendingValue`). */
  earmarkedValue?: number;
  /** ESTATE ownership model: the sum of `person_owned` asset values,
   *  tenant-wide — assets in the estate with no trust intent, carrying no
   *  trust anchor. `0` on none / absent from an old backend. */
  personOwnedValue?: number;
  /** ESTATE ownership model: the FULL estate headline — registered (summed
   *  across ALL the user's trusts) + `earmarkedValue` + `personOwnedValue`.
   *  NEVER re-derived client-side when present — the hero reads this directly
   *  rather than summing `totalValue` + the other two (which would silently
   *  drop any OTHER trust's registered value). Absent from an old backend —
   *  callers fall back to the pre-ESTATE-model day-1 estimate
   *  (`totalValue + claimedPendingValue`). */
  estateValue?: number;
  /** PER-FIRM VISIBILITY SCOPES (Paul + Beno, 2026-08-23) — present ONLY when
   *  this session is a firm acting-as a client who has withheld something.
   *
   *  ⚠ `withheld.netWorth` means `registeredValue` / `earmarkedValue` /
   *  `personOwnedValue` / `estateValue` are absent BY THE CLIENT'S CHOICE — NOT
   *  because this is an old backend and NOT because the client owns nothing.
   *  Every "absent from an old backend, derive it as…" fallback documented on
   *  the fields above MUST be skipped when this flag is set: deriving
   *  `totalValue + claimedPendingValue` there would put a fabricated estate
   *  headline exactly where the client asked for none. `totalValue` stays —
   *  it is the primary trust's own figure, which the firm administers. */
  withheld?: { netWorth?: true; assetList?: true };
  /** What `totalValue` reflects (Valuations Wave 1 / B4): `'holdings'` = live
   *  holdings sum; `'afs'` = the AFS NAV stands. Absent from an old backend. */
  totalValueBasis?: 'holdings' | 'afs';
  /** AFS-GRADING finding 7 — the VALUATION's own as-at date (ISO `YYYY-MM-DD`),
   *  never `lastUpdated`. The hero used to label an AFS net asset value taken
   *  from 28 Feb 2025 statements "as at Aug 2026" (the recalculation stamp).
   *  `null`/absent means the backend cannot date this total honestly — render
   *  NO date rather than substituting one. */
  totalValueAsAt?: string | null;
  /** AFS-GRADING findings 1 + 2 — the confirmed annual-financial-statements
   *  record for the primary trust, WHOLE RAND. `distributionsToBeneficiaries`
   *  lives here because an AFS reports one year-end TOTAL and a distribution row
   *  needs a named beneficiary, a date and a payout status. `null`/absent when
   *  no AFS has been confirmed (or on an old backend). */
  afsFinancials?: {
    periodEndDate: string | null;
    netAssetValue?: number;
    totalAssets?: number;
    totalLiabilities?: number;
    totalIncome?: number;
    totalExpenses?: number;
    distributionsToBeneficiaries?: number;
  } | null;
  yoyPct: number;
  yoyAmount: number;
  /** TRS-03 — the review clock for the headline trust, ABSENT (both keys) when
   *  no initial FICA is on file. See `src/lib/fica-review.ts`. */
  nextFicaReview?: string;
  ficaDaysOut?: number;
  /** TRS-03: `false` = no initial FICA (the clock above is absent), `null` =
   *  the register could not be read. Absent from an older backend. */
  initialFicaOnFile?: boolean | null;
  lastUpdated: string;
  correlationId: string;
}

/** Query key + fetcher shared by the hook AND the auth-time dashboard
 *  prefetch (`prefetch-dashboard.ts`) — one definition, so the prefetched
 *  cache entry is guaranteed to be the exact one the dashboard reads. */
export const DASHBOARD_SUMMARY_QUERY_KEY = ['dashboard', 'summary'] as const;

export function fetchDashboardSummary(signal?: AbortSignal): Promise<DashboardSummary> {
  return api<DashboardSummary>('/api/v1/dashboard/summary', { signal });
}

export function useDashboardSummary(opts: { enabled?: boolean } = {}) {
  return useQuery<DashboardSummary>({
    queryKey: DASHBOARD_SUMMARY_QUERY_KEY,
    queryFn: ({ signal }) => fetchDashboardSummary(signal),
    enabled: opts.enabled ?? true,
    // TRUSTEE-09: a 404 (no trust yet) / 403 (no tenant assignment) / 5xx is
    // not retryable — the default 3x retry just delays the dashboard settling
    // into its empty-state and spams the console. A 404/403 is a terminal
    // "nothing to summarise" signal the caller turns into a set-up CTA; only
    // transient sub-500 network blips are worth retrying.
    retry: failFastOn4xxAnd5xx,
  });
}

// ── /v1/app/dashboard/holdings ───────────────────────────────────────────────

export type HoldingAssetClass =
  | 'equity-listed'
  | 'equity-private'
  | 'fixed-income'
  | 'cash'
  | 'property'
  | 'alternatives'
  | 'other';

export interface HoldingRow {
  id: string;
  assetName: string;
  isin: string | null;
  custodian: string | null;
  unitsHeld: number;
  marketValueZar: number;
  weightBps: number;
  asAt: string;
}

export interface AssetClassBreakdown {
  assetClass: HoldingAssetClass;
  totalMarketValueZar: number;
  weightBps: number;
  holdings: HoldingRow[];
}

export interface DashboardHoldingsResponse {
  trustId: string;
  asAt: string | null;
  totalMarketValueZar: number;
  byAssetClass: AssetClassBreakdown[];
  correlationId: string;
}

/** Key + fetcher shared with the auth-time prefetch — see
 *  DASHBOARD_SUMMARY_QUERY_KEY's note. */
export function dashboardHoldingsQueryKey(trustId: string | undefined) {
  return ['dashboard', 'holdings', trustId ?? null] as const;
}

export function fetchDashboardHoldings(
  trustId: string,
  signal?: AbortSignal,
): Promise<DashboardHoldingsResponse> {
  return api<DashboardHoldingsResponse>(
    `/api/v1/app/dashboard/holdings?trustId=${encodeURIComponent(trustId)}`,
    { signal },
  );
}

/** @param opts.enabled Pass `false` to skip the call. Serves the `financials`
 *  surface — the corpus is not a beneficiary's to see until it is distributed,
 *  so their screens must not request it at all. */
export function useDashboardHoldings(
  trustId: string | undefined,
  opts: { enabled?: boolean } = {},
) {
  return useQuery<DashboardHoldingsResponse>({
    queryKey: dashboardHoldingsQueryKey(trustId),
    queryFn: ({ signal }) => fetchDashboardHoldings(trustId!, signal),
    enabled: (opts.enabled ?? true) && !!trustId,
    retry: failFastOn5xx,
  });
}

// ── /v1/app/dashboard/allocation ─────────────────────────────────────────────

export type AllocationRowStatus = 'within-band' | 'rebalance-buy' | 'rebalance-sell';

export interface AllocationDeltaRow {
  assetClass: HoldingAssetClass;
  targetBps: number;
  toleranceBps: number;
  actualBps: number;
  actualMarketValueZar: number;
  deltaBps: number;
  status: AllocationRowStatus;
}

export interface DashboardAllocationResponse {
  trustId: string;
  asAt: string | null;
  totalMarketValueZar: number;
  ipsAsOf: string | null;
  ipsDocId: string | null;
  reviewedAt: string | null;
  rows: AllocationDeltaRow[];
  correlationId: string;
}

export function useDashboardAllocation(trustId: string | undefined) {
  return useQuery<DashboardAllocationResponse>({
    queryKey: ['dashboard', 'allocation', trustId ?? null],
    queryFn: ({ signal }) =>
      api<DashboardAllocationResponse>(
        `/api/v1/app/dashboard/allocation?trustId=${encodeURIComponent(trustId!)}`,
        { signal },
      ),
    enabled: !!trustId,
    retry: failFastOn5xx,
  });
}

// ── /v1/app/dashboard/distributions-rhythm ───────────────────────────────────

export type QuarterPointStatus = 'paid' | 'partial' | 'pending' | 'future';

export interface QuarterPoint {
  /** Display-friendly label, e.g. 'Q1 2026'. */
  label: string;
  /** 1-based index across the response window (1 = oldest, 8 = newest). */
  index: number;
  startDate: string;
  endDate: string;
  paidAmountCents: number;
  pendingAmountCents: number;
  count: number;
  status: QuarterPointStatus;
}

export interface DashboardDistributionsRhythmResponse {
  trustId: string;
  quarters: QuarterPoint[];
  totalPaidCents: number;
  totalPendingCents: number;
  correlationId: string;
}

export function useDashboardDistributionsRhythm(trustId: string | undefined) {
  return useQuery<DashboardDistributionsRhythmResponse>({
    queryKey: ['dashboard', 'distributions-rhythm', trustId ?? null],
    queryFn: ({ signal }) =>
      api<DashboardDistributionsRhythmResponse>(
        `/api/v1/app/dashboard/distributions-rhythm?trustId=${encodeURIComponent(trustId!)}`,
        { signal },
      ),
    enabled: !!trustId,
  });
}

// ── /v1/app/dashboard/trust-summary ──────────────────────────────────────────

/** `unverified` (TRS-03): nobody on the register is verified, so no review
 *  cycle exists yet. */
export type FicaStatus = 'unverified' | 'current' | 'due-soon' | 'overdue';

export interface DashboardTrustSummary {
  trustId: string;
  trustName: string;
  registration: string;
  deedDate: string | null;
  totalValue: number;
  yoyPct: number;
  yoyAmount: number;
  lastUpdated: string;
  /** TRS-03 — ABSENT (both keys) when `ficaStatus` is `'unverified'`. */
  nextFicaReview?: string;
  ficaDaysOut?: number;
  ficaStatus: FicaStatus;
  /** TRS-03: absent from an older backend. */
  initialFicaOnFile?: boolean | null;
  trusteesCount: number;
  beneficiariesActiveCount: number;
  openResolutionsCount: number;
  awaitingMySignatureCount: number;
  lastResolutionRef: string | null;
  lastResolutionAt: string | null;
  lastDistributionAt: string | null;
  lastDistributionAmountCents: number | null;
  loaEffectiveFrom: string | null;
  loaMasterReference: string | null;
  /** Valuations Wave 1 (B3) staleness rollup — how many of the trust's holdings
   *  are past their revaluation due date. Absent from an old backend: the
   *  "Values needing attention" card then falls back to a client-side estimate. */
  staleHoldingsCount?: number;
  /** Oldest holding `asAt` across the trust (ISO date), or null when no valued
   *  holdings. Optional — Wave 1 backend only. */
  oldestAsAt?: string | null;
  /** What the trust's `totalValue` reflects (B4) — see `DashboardSummary`. */
  totalValueBasis?: 'holdings' | 'afs';
  correlationId: string;
}

export function useDashboardTrustSummary(trustId: string | undefined) {
  return useQuery<DashboardTrustSummary>({
    queryKey: ['dashboard', 'trust-summary', trustId ?? null],
    queryFn: ({ signal }) =>
      api<DashboardTrustSummary>(
        `/api/v1/app/dashboard/trust-summary?trustId=${encodeURIComponent(trustId!)}`,
        { signal },
      ),
    enabled: !!trustId,
  });
}

// ── ESTATE ownership model — the 3-way split (2026-07-11 ruling) ────────────

export interface EstateSplitPart {
  key: 'registered' | 'earmarked' | 'personal';
  /** Plain-English clause, e.g. "registered to trusts". */
  label: string;
  /** Whole Rand. */
  valueRand: number;
}

/**
 * The 3-way split behind the hero's "R x registered to trusts · R y transfer
 * in progress · R z personal" line (and the estate-assets card's totals
 * strip) — PURE: takes whole-Rand numbers already computed server-side
 * (`dashboard-summary` / `estate-assets-list`) and returns the parts to
 * render. Zero-suppresses `earmarked` / `personal` when they are 0 (an
 * "R 0 personal" clause reads oddly); `registered` is ALWAYS included — it is
 * the honest AUM anchor and must never look silently omitted, even at R0
 * (mirrors the pre-existing "Registered in the trust: R 0" day-1 line, same
 * principle). Order is fixed: registered, earmarked, personal.
 */
export function estateSplitParts(input: {
  registeredRand: number;
  earmarkedRand: number;
  personOwnedRand: number;
}): EstateSplitPart[] {
  const parts: EstateSplitPart[] = [
    { key: 'registered', label: 'registered to trusts', valueRand: input.registeredRand },
  ];
  if (input.earmarkedRand > 0) {
    parts.push({ key: 'earmarked', label: 'transfer in progress', valueRand: input.earmarkedRand });
  }
  if (input.personOwnedRand > 0) {
    parts.push({ key: 'personal', label: 'personal', valueRand: input.personOwnedRand });
  }
  return parts;
}

// ── Display helpers ──────────────────────────────────────────────────────────

// `formatZarCents` (cents -> "R 123 456,78") is the canonical ZAR
// formatter and now lives in `lib/fmt-r.ts`; it is re-exported at the top
// of this module so existing `from '../../lib/api'` imports keep
// resolving (CUX-1, 2026-05-21).

/** Basis points to percentage display string, e.g. 6500 -> "65.00%".
 * Two decimals: used for allocation + holding-share percentages (CUX-9). */
export function formatBps(bps: number): string {
  return `${(bps / 100).toFixed(2)}%`;
}

/** Human-friendly asset-class label (the backend ships kebab-case enums). */
export function assetClassLabel(cls: HoldingAssetClass): string {
  switch (cls) {
    case 'equity-listed':
      return 'Listed equity';
    case 'equity-private':
      return 'Private equity';
    case 'fixed-income':
      return 'Fixed income';
    case 'cash':
      return 'Cash';
    case 'property':
      return 'Property';
    case 'alternatives':
      return 'Alternatives';
    case 'other':
      return 'Other';
  }
}

/** CSS variable mapping for the donut + allocation rows, keyed by the
 *  backend's HoldingAssetClass enum. Aligns with the legacy ALLOCATION
 *  swatch palette so the visual identity carries over. */
export function assetClassColorVar(cls: HoldingAssetClass): string {
  switch (cls) {
    case 'equity-listed':
    case 'equity-private':
      return 'var(--primary-700)';
    case 'fixed-income':
      return 'var(--primary-500)';
    case 'property':
      return 'var(--accent-500)';
    case 'cash':
      return 'var(--primary-300)';
    case 'alternatives':
      return 'var(--accent-300)';
    case 'other':
      return 'var(--ink-3)';
  }
}
